<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { vMaska } from 'maska/vue';
  import type { MaskInputOptions } from 'maska';
  import { IconButton } from '@/shared/ui';
  import EyeIcon from '@/shared/assets/icons/Eye.svg';
  import EyeDisabledIcon from '@/shared/assets/icons/EyeDisabled.svg';

  const props = defineProps<{
    label: string;
    type: string;
    disabled?: boolean;
    placeholder: string;
    labelClass?: string;
    id: string;
    error?: string;
    modelValue: string;
    isPassword?: boolean;
    inputClass?: string;
    maxLength?: number;
    mask?: MaskInputOptions;
  }>();

  const emit = defineEmits(['update:modelValue', 'blur']);

  const passwordVisible = ref(false);
  const inputType = ref(props.type);

  const resolvedType = computed(() => (props.isPassword ? inputType.value : props.type));

  const inputClasses = computed(() => [
    'border rounded-md py-3 px-4 w-full',
    { 'border-red-16': props.error, 'border-gray-02': !props.error, 'pr-10': props.isPassword },
    props.inputClass,
  ]);

  const onInput = (event: Event) => {
    emit('update:modelValue', (event.target as HTMLInputElement).value);
  };

  const togglePasswordVisibility = () => {
    passwordVisible.value = !passwordVisible.value;
    inputType.value = passwordVisible.value ? 'text' : 'password';
  };
</script>

<template>
  <div class="flex flex-col gap-2">
    <label
      :for="id"
      class="font-medium"
      :class="labelClass"
    >{{ label }}</label>
    <div class="relative">
      <input
        :id="id"
        v-maska="mask"
        :type="resolvedType"
        :class="inputClasses"
        :placeholder="placeholder"
        :disabled="disabled"
        :value="modelValue"
        :maxlength="maxLength"
        @input="onInput"
        @blur="$emit('blur')"
      >
      <div
        v-if="isPassword"
        class="absolute inset-y-0 right-0 flex items-center p-2"
      >
        <IconButton
          :src="passwordVisible ? EyeIcon : EyeDisabledIcon"
          :alt="passwordVisible ? 'Hide password' : 'Show password'"
          :on-click="togglePasswordVisibility"
        />
      </div>
    </div>
    <p
      v-if="error"
      class="text-red-16 text-sm/5"
    >
      {{ error }}
    </p>
  </div>
</template>
